import type { StockAnalysisLocale } from './stockAnalysis';

const en = {
  suite: {
    eyebrow: 'Hanggent Stock Intelligence',
    title: 'Four independent stock intelligence projects',
    description:
      'Open each capability directly from the navigation while keeping authenticated user data isolated.',
    tenantIsolation: 'Authenticated, per-user data isolation',
    stage: 'Stage {{number}}',
    open: 'Open workspace',
    unavailable: 'Unavailable in this deployment',
    alphaRole: 'Discover candidates',
    alphaName: 'AlphaSift',
    alphaDescription:
      'Find lifecycle candidates with five-year and full-history, weekly and monthly confirmation.',
    researchRole: 'Research candidates',
    researchName: 'Vibe Research',
    researchLoading: 'Loading Vibe Research',
    researchDescription:
      'Review valuation, financials, filings, fund flows, news, reports, and bull-versus-bear research.',
    astockRole: 'Read the market environment',
    astockName: 'Vibe AStock',
    astockLoading: 'Loading Vibe AStock',
    astockDescription:
      'Place candidates in the A-share short-term cycle using breadth, limit-up ladders, and sentiment.',
  },
  alphaDaily: {
    title: 'Daily lifecycle scan',
    description:
      'Classifies A/B/C/D/E/F/G/H independently over five years and all available history, then keeps matching stages that pass financial scoring and rank in their industry top five.',
    refresh: 'Refresh',
    downloadHtml: 'Download HTML',
    matchingCandidates: 'Matching candidates',
    financiallyEligible: 'Financially eligible',
    industrySelections: 'Industry selections',
    cnDate: 'A-share date',
    usDate: 'US date',
    historyDate: 'Historical scan date',
    allMarkets: 'All markets',
    aShares: 'A-Shares',
    usStocks: 'US Stocks',
    allStages: 'All stages',
    allIndustries: 'All industries',
    minScore: 'Min score',
    marketCap: 'Market cap',
    marketCapAny: 'Any market cap',
    marketCapOver50: '≥ 5B',
    marketCapOver100: '≥ 10B',
    marketCapOver500: '≥ 50B',
    marketCapOver1000: '≥ 100B',
    searchPlaceholder: 'Symbol, company, or industry',
    search: 'Search',
    market: 'Market',
    industry: 'Industry',
    rank: 'Rank',
    symbol: 'Symbol',
    company: 'Company',
    stage: 'Stage',
    combinedScore: 'Combined',
    fundamentalScore: 'Fundamental',
    patternScore: 'Pattern',
    price: 'Price',
    marketDate: 'Market date',
    financialPeriod: 'Financial period',
    empty: 'No results match the current filters.',
    showing: 'Showing {{visible}} of {{total}}',
    rowsPerPage: 'Rows per page',
    showingRange: 'Showing {{from}}–{{to}} of {{total}}',
    pageStatus: 'Page {{page}} of {{pages}}',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    coverage:
      'Scanned {{cn}} A-shares and {{us}} US stocks. Research only; not investment advice.',
    manualTitle: 'Run a strategy manually',
    manualDescription:
      'Run an installed AlphaSift strategy on demand and save the result to your account.',
  },
};

const zhHans = {
  suite: {
    eyebrow: 'Hanggent 股票智能',
    title: '四个独立股票智能项目',
    description: '可从导航直接打开各项能力，并保持登录用户的数据相互隔离。',
    tenantIsolation: '已认证，按用户隔离数据',
    stage: '阶段 {{number}}',
    open: '打开工作区',
    unavailable: '当前部署不可用',
    alphaRole: '发现候选股票',
    alphaName: 'AlphaSift',
    alphaDescription:
      '使用5年与全历史、周K与月K交叉确认，寻找生命周期候选股票。',
    researchRole: '研究候选股票',
    researchName: 'Vibe Research',
    researchLoading: '正在加载 Vibe Research',
    researchDescription: '研究估值、财务、公告、资金流、新闻、研报及多空观点。',
    astockRole: '判断市场环境',
    astockName: 'Vibe AStock',
    astockLoading: '正在加载 Vibe AStock',
    astockDescription: '根据市场宽度、涨停梯队和情绪判断A股短线周期。',
  },
  alphaDaily: {
    title: '每日生命周期扫描',
    description:
      '分别使用最近5年和全部可用历史判断A/B/C/D/E/F/G/H，仅保留阶段一致、通过财务评分且进入行业前5的股票。',
    refresh: '刷新',
    downloadHtml: '下载HTML',
    matchingCandidates: '一致候选',
    financiallyEligible: '财务合格',
    industrySelections: '行业入选',
    cnDate: 'A股日期',
    usDate: '美股日期',
    historyDate: '历史扫描日期',
    allMarkets: '全部市场',
    aShares: 'A股',
    usStocks: '美股',
    allStages: '全部阶段',
    allIndustries: '全部行业',
    minScore: '最低评分',
    marketCap: '市值',
    marketCapAny: '不限市值',
    marketCapOver50: '≥ 50亿',
    marketCapOver100: '≥ 100亿',
    marketCapOver500: '≥ 500亿',
    marketCapOver1000: '≥ 1000亿',
    searchPlaceholder: '代码、名称或行业',
    search: '查询',
    market: '市场',
    industry: '行业',
    rank: '排名',
    symbol: '代码',
    company: '公司',
    stage: '阶段',
    combinedScore: '综合分',
    fundamentalScore: '基本面分',
    patternScore: '形态分',
    price: '价格',
    marketDate: '行情日',
    financialPeriod: '财报期',
    empty: '没有符合当前筛选条件的结果。',
    showing: '当前显示 {{visible}} / {{total}} 条',
    rowsPerPage: '每页条数',
    showingRange: '显示第 {{from}}–{{to}} 条，共 {{total}} 条',
    pageStatus: '第 {{page}} / {{pages}} 页',
    previousPage: '上一页',
    nextPage: '下一页',
    coverage:
      '已扫描 {{cn}} 只A股和 {{us}} 只美股。结果仅供研究，不构成投资建议。',
    manualTitle: '手动运行策略',
    manualDescription:
      '按需运行已安装的AlphaSift策略，并将结果保存到你的账户。',
  },
};

const zhHant = {
  suite: {
    ...zhHans.suite,
    title: '四個獨立股票智能項目',
    description: '可從導覽直接開啟各項能力，並保持登入使用者的資料相互隔離。',
    tenantIsolation: '已驗證，按使用者隔離資料',
    stage: '階段 {{number}}',
    open: '開啟工作區',
  },
  alphaDaily: {
    ...zhHans.alphaDaily,
    title: '每日生命週期掃描',
    description:
      '分別使用最近5年和全部可用歷史判斷A/B/C/D/E/F/G/H，僅保留階段一致、通過財務評分且進入行業前5的股票。',
    refresh: '重新整理',
    downloadHtml: '下載HTML',
    allMarkets: '全部市場',
    allStages: '全部階段',
    allIndustries: '全部行業',
    search: '查詢',
    empty: '沒有符合目前篩選條件的結果。',
    manualTitle: '手動執行策略',
    manualDescription:
      '按需執行已安裝的AlphaSift策略，並將結果儲存至你的帳戶。',
  },
};

export const stockProductTranslations: Record<StockAnalysisLocale, typeof en> =
  {
    'en-US': en,
    'zh-Hans': zhHans,
    'zh-Hant': zhHant,
    de: en,
    ko: en,
    ja: en,
    fr: en,
    ru: en,
    it: en,
    ar: en,
    es: en,
    hi: en,
  };
